/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import promptLibraryMessages from '../../../../ui/prompt-library/messages';

const styles = cssMap({
	// Skeleton styles matching PromptCardsGrid card dimensions
	skeletonGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fill, minmax(245px, 1fr))',
		gap: token('space.200'),
		paddingTop: token('space.200'),
		paddingLeft: token('space.100'),
	},
	skeletonCard: {
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
		paddingTop: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		width: '245px',
		height: '150px',
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
	},
	skeletonCardContent: {
		height: '100%',
	},
	skeletonCardIcon: {
		paddingBottom: token('space.100'),
	},
});

const SKELETON_CARD_COUNT = 6;

const PromptCardSkeleton = ({ testId = 'prompt-card-skeleton' }: { testId?: string }) => (
	<Box xcss={cx(styles.skeletonCard)} testId={testId} role="presentation">
		<Stack xcss={styles.skeletonCardContent}>
			<Flex xcss={styles.skeletonCardIcon}>
				<Skeleton
					width="32px"
					height="32px"
					borderRadius={token('radius.small')}
					isShimmering
					testId={`${testId}-icon`}
				/>
			</Flex>
			<Stack space="space.100">
				<Skeleton
					width="80%"
					height="14px"
					borderRadius={token('radius.small')}
					isShimmering
					testId={`${testId}-title`}
				/>
				<Stack space="space.050">
					<Skeleton
						width="100%"
						height="12px"
						borderRadius={token('radius.small')}
						isShimmering
						testId={`${testId}-text-1`}
					/>
					<Skeleton
						width="70%"
						height="12px"
						borderRadius={token('radius.small')}
						isShimmering
						testId={`${testId}-text-2`}
					/>
				</Stack>
			</Stack>
		</Stack>
	</Box>
);

export const PromptCardsGridSkeleton = ({
	testId = 'prompt-cards-grid-skeleton',
}: {
	testId?: string;
}): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Box
			xcss={styles.skeletonGrid}
			testId={testId}
			role="status"
			aria-busy="true"
			aria-label={formatMessage(promptLibraryMessages.loadingPrompts)}
		>
			{Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
				<PromptCardSkeleton key={index} testId={`${testId}-card-${index}`} />
			))}
		</Box>
	);
};
